<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '$shared/api';
  import { addToast } from '$shared/toasts.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import Avatar from '$shared/Avatar.svelte';

  interface BlockedUser {
    id: number;
    username: string;
    avatar_url: string | null;
    reason: string | null;
    created_at: string;
  }

  let blocks = $state<BlockedUser[]>([]);
  let loading = $state(true);

  onMount(load);

  async function load() {
    try {
      const data = await api.get<{ blocks: BlockedUser[] }>('/users/current/blocks');
      blocks = data.blocks;
    } catch {
      addToast('Could not load blocked users', 'error');
    }
    loading = false;
  }

  async function unblock(user: BlockedUser) {
    try {
      await api.delete(`/users/${user.id}/block`);
      blocks = blocks.filter((b) => b.id !== user.id);
      addToast(`Unblocked @${user.username}`, 'join');
    } catch {
      addToast('Could not unblock', 'error');
    }
  }

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
</script>

<div class="narrow">
  <h1>Blocked Users</h1>
  <p class="intro">
    Blocked users can't vote on your tracks, follow you, challenge you, or send you notifications. They won't appear in
    your feed, leaderboards, or search.
  </p>

  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else if blocks.length === 0}
    <div class="card card--no-hover empty-state">
      <p>You haven't blocked anyone.</p>
    </div>
  {:else}
    <p class="count">{blocks.length} blocked user{blocks.length === 1 ? '' : 's'}</p>
    <div class="block-list">
      {#each blocks as u (u.id)}
        <div class="block-card card card--no-hover">
          <a class="block-card__user" href="#/users/{u.username}">
            <Avatar username={u.username} avatarUrl={u.avatar_url} size="sm" />
            <div class="block-card__info">
              <div class="block-card__name">@{u.username}</div>
              <div class="block-card__meta">
                Blocked {formatDate(u.created_at)}
                {#if u.reason}· {u.reason}{/if}
              </div>
            </div>
          </a>
          <button class="btn btn-outline btn-sm" onclick={() => unblock(u)}>Unblock</button>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .narrow {
    max-width: 40rem;
    margin: 0 auto;
  }
  h1 {
    margin-bottom: 0.5rem;
  }
  .intro {
    color: var(--text-muted);
    font-size: 0.85rem;
    line-height: 1.45;
    margin: 0 0 1.25rem;
  }
  .count {
    color: var(--text-muted);
    font-size: 0.85rem;
    margin: 0 0 0.75rem;
  }
  .empty-state {
    text-align: center;
    padding: 2rem;
    color: var(--text-secondary);
  }

  .block-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .block-card {
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }
  .block-card__user {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex: 1;
    min-width: 0;
    color: inherit;
    text-decoration: none;
  }
  .block-card__user:hover .block-card__name {
    color: var(--cyan);
  }
  .block-card__info {
    flex: 1;
    min-width: 0;
  }
  .block-card__name {
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--text);
  }
  .block-card__meta {
    font-size: 0.8rem;
    color: var(--text-muted);
    margin-top: 0.15rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
